/**
 * Bidirectional Sync Operations
 * 
 * Implements upload (local to remote) and download (remote to local) 
 * sync operations with proper error handling and conflict detection.
 */

import { supabase } from '@/integrations/supabase/client';
import { indexedDBService } from '../indexeddb';
import { conflictResolver } from './ConflictResolver';
import {
  TrainingSessionTransformer,
  TechniqueTransformer,
  UserTechniqueTransformer,
  PositionTransformer,
  GamePlanTransformer,
  UserProfileTransformer,
  RoadmapMacroTransformer,
  RoadmapMesoTransformer,
  DataTransformUtils
} from './DataTransformer';
import type { SyncQueueItem } from '../indexeddb/schema';

/**
 * Sync operation result
 */
export interface SyncOperationResult {
  success: boolean;
  processed: number;
  failed: number;
  conflicts: number;
  errors: string[];
}

/**
 * Table sync configuration
 */
interface TableSyncConfig {
  tableName: string;
  supabaseTable: string;
  transformer: {
    fromSupabase?: (row: unknown) => unknown;
    toSupabaseInsert?: (data: unknown) => unknown;
    toSupabaseUpdate?: (data: unknown) => unknown;
  };
  userFiltered: boolean; // Whether to filter by user_id
  selectFields?: string; // Custom select fields
}

/**
 * Bidirectional Sync Operations Service
 */
export class SyncOperations {
  private readonly tableConfigs: TableSyncConfig[] = [
    {
      tableName: 'training_sessions',
      supabaseTable: 'training_sessions',
      transformer: TrainingSessionTransformer as unknown as { fromSupabase?: (row: unknown) => unknown; toSupabaseInsert?: (data: unknown) => unknown; toSupabaseUpdate?: (data: unknown) => unknown; },
      userFiltered: true
    },
    {
      tableName: 'techniques',
      supabaseTable: 'techniques',
      transformer: TechniqueTransformer as unknown as { fromSupabase?: (row: unknown) => unknown; toSupabaseInsert?: (data: unknown) => unknown; toSupabaseUpdate?: (data: unknown) => unknown; },
      userFiltered: false
    },
    {
      tableName: 'user_techniques',
      supabaseTable: 'user_techniques',
      transformer: UserTechniqueTransformer as unknown as { fromSupabase?: (row: unknown) => unknown; toSupabaseInsert?: (data: unknown) => unknown; toSupabaseUpdate?: (data: unknown) => unknown; },
      userFiltered: true
    },
    {
      tableName: 'positions',
      supabaseTable: 'positions',
      transformer: PositionTransformer as unknown as { fromSupabase?: (row: unknown) => unknown; toSupabaseInsert?: (data: unknown) => unknown; toSupabaseUpdate?: (data: unknown) => unknown; },
      userFiltered: false
    },
    {
      tableName: 'game_plans',
      supabaseTable: 'game_plans',
      transformer: GamePlanTransformer as unknown as { fromSupabase?: (row: unknown) => unknown; toSupabaseInsert?: (data: unknown) => unknown; toSupabaseUpdate?: (data: unknown) => unknown; },
      userFiltered: true
    },
    {
      tableName: 'user_profile',
      supabaseTable: 'profiles',
      transformer: UserProfileTransformer as unknown as { fromSupabase?: (row: unknown) => unknown; toSupabaseInsert?: (data: unknown) => unknown; toSupabaseUpdate?: (data: unknown) => unknown; },
      userFiltered: false, // Profiles table uses 'id' not 'user_id' for filtering
      selectFields: '*' // We'll handle user filtering manually for profiles
    },
    {
      tableName: 'roadmap_macro',
      supabaseTable: 'roadmap_macro',
      transformer: RoadmapMacroTransformer as unknown as { fromSupabase?: (row: unknown) => unknown; toSupabaseInsert?: (data: unknown) => unknown; toSupabaseUpdate?: (data: unknown) => unknown; },
      userFiltered: false
    },
    {
      tableName: 'roadmap_meso',
      supabaseTable: 'roadmap_meso',
      transformer: RoadmapMesoTransformer as unknown as { fromSupabase?: (row: unknown) => unknown; toSupabaseInsert?: (data: unknown) => unknown; toSupabaseUpdate?: (data: unknown) => unknown; },
      userFiltered: false
    }
  ];

  /**
   * Upload local changes to remote (Supabase)
   */
  async uploadLocalChanges(): Promise<SyncOperationResult> {

    const result: SyncOperationResult = {
      success: true,
      processed: 0,
      failed: 0,
      conflicts: 0,
      errors: []
    };

    try {
      // Get current user - but with better error handling for race conditions
      const { data: { user }, error } = await supabase.auth.getUser();
      let userId: string;

      if (error || !user) {
        // If auth fails during sync, it might be a race condition - wait and retry once
        console.log('⚠️ Auth failed during sync, retrying in 2 seconds...', error?.message);
        await new Promise(resolve => setTimeout(resolve, 2000));

        const { data: { user: retryUser }, error: retryError } = await supabase.auth.getUser();
        if (retryError || !retryUser) {
          throw new Error(`No authenticated user found after retry: ${retryError?.message || 'Unknown error'}`);
        }

        console.log('✅ Auth retry successful');
        userId = retryUser.id;
      } else {
        userId = user.id;
      }

      // Process sync queue items
      const pendingItems = await indexedDBService.syncQueue.getPendingItems();

      for (const item of pendingItems) {
        try {
          await indexedDBService.syncQueue.markAsProcessing(item.id);
          
          const uploadResult = await this.uploadSyncItem(item, userId);
          
          if (uploadResult.success) {
            await indexedDBService.syncQueue.markAsCompleted(item.id);
            result.processed++;
          } else {
            await indexedDBService.syncQueue.markAsFailed(item.id, uploadResult.error || 'Unknown error');
            result.failed++;
            result.errors.push(`${item.table_name}/${item.record_id}: ${uploadResult.error}`);
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          await indexedDBService.syncQueue.markAsFailed(item.id, errorMessage);
          result.failed++;
          result.errors.push(`${item.table_name}/${item.record_id}: ${errorMessage}`);
        }
      }

      result.success = result.failed === 0;
      
    } catch (error) {
      console.error('❌ Upload failed:', error);
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
    }

    return result;
  }

  /**
   * Upload a single sync queue item
   */
  private async uploadSyncItem(item: SyncQueueItem, _userId: string): Promise<{ success: boolean; error?: string }> {
    const config = this.tableConfigs.find(c => c.tableName === item.table_name);
    if (!config) {
      return { success: false, error: `Unknown table: ${item.table_name}` };
    }

    try {
      switch (item.operation) {
        case 'create':
          return await this.uploadCreate(config, (item.data as Record<string, unknown>) || {});

        case 'update':
          return await this.uploadUpdate(config, item.record_id, (item.data as Record<string, unknown>) || {});
          
        case 'delete':
          return await this.uploadDelete(config, item.record_id);
          
        default:
          return { success: false, error: `Unknown operation: ${item.operation}` };
      }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Upload create operation
   */
  private async uploadCreate(config: TableSyncConfig, data: Record<string, unknown>): Promise<{ success: boolean; error?: string }> {
    try {
      const insertData = config.transformer.toSupabaseInsert 
        ? config.transformer.toSupabaseInsert(data)
        : DataTransformUtils.stripOfflineMetadata(data as Record<string, unknown>);

      const { error } = await supabase
        .from(config.supabaseTable)
        .insert(insertData);

      if (error) {
        return { success: false, error: error.message };
      }

      // Update local record to mark as synced
      const syncedData = DataTransformUtils.addOfflineMetadata(data as Record<string, unknown>, 'synced');
      await indexedDBService.offlineDB.put(config.tableName, syncedData);

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Upload update operation
   */
  private async uploadUpdate(config: TableSyncConfig, recordId: string, data: Record<string, unknown>): Promise<{ success: boolean; error?: string }> {
    try {
      const updateData = config.transformer.toSupabaseUpdate 
        ? config.transformer.toSupabaseUpdate(data)
        : DataTransformUtils.stripOfflineMetadata(data as Record<string, unknown>);

      const { error } = await supabase
        .from(config.supabaseTable)
        .update(updateData)
        .eq('id', recordId);

      if (error) {
        return { success: false, error: error.message };
      }

      // Update local record to mark as synced
      const syncedData = DataTransformUtils.addOfflineMetadata(data as Record<string, unknown>, 'synced');
      await indexedDBService.offlineDB.put(config.tableName, syncedData);

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Upload delete operation
   */
  private async uploadDelete(config: TableSyncConfig, recordId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from(config.supabaseTable)
        .delete()
        .eq('id', recordId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Download remote changes to local (IndexedDB)
   */
  async downloadRemoteChanges(selectiveSync = true): Promise<SyncOperationResult> {
    console.log('📥 Starting download of remote changes...');
    
    const result: SyncOperationResult = {
      success: true,
      processed: 0,
      failed: 0,
      conflicts: 0,
      errors: []
    };

    try {
      // Get current user from Supabase auth (more reliable than cache)
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user?.id) {
        throw new Error('No authenticated user found in session');
      }

      const userId = session.user.id;

      // Download each table
      for (const config of this.tableConfigs) {
        try {
          const tableResult = await this.downloadTable(config, userId, selectiveSync);
          result.processed += tableResult.processed;
          result.failed += tableResult.failed;
          result.conflicts += tableResult.conflicts;
          result.errors.push(...tableResult.errors);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          result.failed++;
          result.errors.push(`${config.tableName}: ${errorMessage}`);
        }
      }

      result.success = result.failed === 0;
      
    } catch (error) {
      console.error('❌ Download failed:', error);
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
    }

    return result;
  }

  /**
   * Download a single table
   */
  private async downloadTable(
    config: TableSyncConfig, 
    userId: string, 
    selectiveSync: boolean
  ): Promise<SyncOperationResult> {
    const result: SyncOperationResult = {
      success: true,
      processed: 0,
      failed: 0,
      conflicts: 0,
      errors: []
    };

    try {
      // Get last sync time for incremental sync
      const metadata = await indexedDBService.syncMetadata.getTableMetadata(config.tableName);
      const lastSync = selectiveSync && metadata ? metadata.last_sync : null;

      // Build query
      let query = supabase.from(config.supabaseTable).select('*');

      // Filter by user if needed
      if (config.userFiltered) {
        query = query.eq('user_id', userId);
      } else if (config.tableName === 'user_profile') {
        // Special handling for profiles table - filter by 'id' not 'user_id'
        query = query.eq('id', userId);
      }

      // Add timestamp filter for incremental sync
      if (lastSync) {
        query = query.gt('updated_at', lastSync);
      }

      // Add date range filter for training sessions (90 days)
      if (config.tableName === 'training_sessions' && selectiveSync) {
        const cutoffDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        query = query.gte('date', cutoffDate);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(error.message);
      }

      if (!data || data.length === 0) {
        console.log(`📥 No new data for ${config.tableName}`);
        return result;
      }

      console.log(`📥 Downloaded ${data.length} records for ${config.tableName}`);

      // Process each record
      for (const remoteRecord of data) {
        try {
          const processResult = await this.processRemoteRecord(config, remoteRecord);
          
          if (processResult.conflict) {
            result.conflicts++;
          } else {
            result.processed++;
          }
        } catch (error) {
          result.failed++;
          result.errors.push(`${config.tableName}/${remoteRecord.id}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      // Update sync metadata
      await indexedDBService.syncMetadata.updateLastSync(config.tableName, new Date().toISOString());

    } catch (error) {
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
    }

    return result;
  }

  /**
   * Process a single remote record
   */
  private async processRemoteRecord(
    config: TableSyncConfig, 
    remoteRecord: { id: string; [key: string]: unknown }
  ): Promise<{ conflict: boolean }> {
    // Transform remote record to offline format
    const offlineRecord = config.transformer.fromSupabase 
      ? config.transformer.fromSupabase!(remoteRecord)
      : { ...remoteRecord, _sync_status: 'synced' };

    // Check if local record exists
    const localRecord = await indexedDBService.offlineDB.get(config.tableName, remoteRecord.id);

    if (!localRecord) {
      // No local record, just insert
      await indexedDBService.offlineDB.put(config.tableName, offlineRecord);
      return { conflict: false };
    }

    // Check for conflicts
    const conflict = await conflictResolver.detectConflict(
      config.tableName,
      remoteRecord.id,
      localRecord as Record<string, unknown>,
      offlineRecord as Record<string, unknown>
    );

    if (conflict) {
      // Conflict detected, try to resolve automatically
      const resolution = await conflictResolver.resolveConflict(conflict);
      
      if (resolution.resolved) {
        // Conflict resolved automatically
        await indexedDBService.offlineDB.put(config.tableName, resolution.finalData);
        return { conflict: false };
      } else {
        // Requires manual resolution
        return { conflict: true };
      }
    } else {
      // No conflict, update local record
      await indexedDBService.offlineDB.put(config.tableName, offlineRecord);
      return { conflict: false };
    }
  }
}

// Export singleton instance
export const syncOperations = new SyncOperations();
